import { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
	Plus,
	Search,
	Download,
	Users,
	MoreVertical,
	Edit2,
	Trash2,
} from 'lucide-react'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'

import { cn, downloadCSV } from '../../lib/utils'
import { useAuth } from '../../providers/AuthProvider'
import { useToast } from '../../components/ui/Toast'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import { PageHeader } from '../../components/ui/PageHeader'
import { Card, CardContent } from '../../components/ui/Card'
import {
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell,
} from '../../components/ui/Table'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from '../../components/ui/Dialog'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import {
	usePartners,
	useCreatePartner,
	useUpdatePartner,
	useDeletePartner,
	type Partner,
	type CreatePartnerData,
	type UpdatePartnerData,
} from '../../hooks/usePartners'

interface PartnerFormData {
	type: 'supplier' | 'customer'
	code: string
	name: string
	nameAr: string
	phone: string
	mobile: string
	email: string
	address: string
	city: string
	country: string
	taxNumber: string
	notes: string
	isActive: boolean
}

const initialFormData: PartnerFormData = {
	type: 'supplier',
	code: '',
	name: '',
	nameAr: '',
	phone: '',
	mobile: '',
	email: '',
	address: '',
	city: '',
	country: '',
	taxNumber: '',
	notes: '',
	isActive: true,
}

interface PartnersPageProps {
	type?: 'supplier' | 'customer'
}

export default function PartnersPage({ type: initialType }: PartnersPageProps = {}) {
	const { t, i18n } = useTranslation()
	const { hasPermission } = useAuth()
	const { toast } = useToast()

	const [page, setPage] = useState(1)
	const [searchInputValue, setSearchInputValue] = useState('')
	const [searchTerm, setSearchTerm] = useState('')
	const [typeFilter, setTypeFilter] = useState<'all' | 'supplier' | 'customer'>(initialType || 'all')
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
	const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null)
	const [formData, setFormData] = useState<PartnerFormData>({
		...initialFormData,
		type: initialType || 'supplier',
	})
	const [formErrors, setFormErrors] = useState<Record<string, string>>({})

	const isRtl = i18n.dir() === 'rtl'

	const {
		data: partnersData,
		isLoading,
		error,
	} = usePartners({
		page,
		limit: 20,
		search: searchTerm || undefined,
		type: typeFilter !== 'all' ? typeFilter : undefined,
	})

	const partners = partnersData?.data ?? []
	const pagination = partnersData?.pagination

	const createPartnerMutation = useCreatePartner()
	const updatePartnerMutation = useUpdatePartner()
	const deletePartnerMutation = useDeletePartner()

	const validateForm = useCallback(
		(data: PartnerFormData): Record<string, string> => {
			const errors: Record<string, string> = {}

			if (!data.code.trim()) {
				errors.code = t('partners.validation.codeRequired', 'Partner code is required')
			}

			if (!data.name.trim()) {
				errors.name = t('partners.validation.nameRequired', 'Name is required')
			}

			return errors
		},
		[t]
	)

	const handleCloseCreateDialog = () => {
		setIsCreateDialogOpen(false)
		setFormData(initialFormData)
		setFormErrors({})
	}

	const handleCloseEditDialog = () => {
		setIsEditDialogOpen(false)
		setSelectedPartner(null)
		setFormData(initialFormData)
		setFormErrors({})
	}

	const handleCloseDeleteDialog = () => {
		setIsDeleteDialogOpen(false)
		setSelectedPartner(null)
	}

	const handleOpenEditDialog = (partner: Partner) => {
		setSelectedPartner(partner)
		setFormData({
			type: partner.type,
			code: partner.code,
			name: partner.name,
			nameAr: partner.nameAr || '',
			phone: partner.contact?.phone || '',
			mobile: partner.contact?.mobile || '',
			email: partner.contact?.email || '',
			address: partner.address?.street || '',
			city: partner.address?.city || '',
			country: partner.address?.country || '',
			taxNumber: partner.accounting?.taxNumber || '',
			notes: partner.notes || '',
			isActive: partner.isActive,
		})
		setIsEditDialogOpen(true)
	}

	const handleOpenDeleteDialog = (partner: Partner) => {
		setSelectedPartner(partner)
		setIsDeleteDialogOpen(true)
	}

	const handleCreatePartner = async () => {
		const errors = validateForm(formData)
		if (Object.keys(errors).length > 0) {
			setFormErrors(errors)
			return
		}

		const createData: CreatePartnerData = {
			type: formData.type,
			code: formData.code,
			name: formData.name,
			nameAr: formData.nameAr || undefined,
			contact: {
				phone: formData.phone || undefined,
				mobile: formData.mobile || undefined,
				email: formData.email || undefined,
			},
			address: {
				street: formData.address || undefined,
				city: formData.city || undefined,
				country: formData.country || undefined,
			},
			accounting: {
				taxNumber: formData.taxNumber || undefined,
			},
			notes: formData.notes || undefined,
			isActive: formData.isActive,
		}

		try {
			await createPartnerMutation.mutateAsync(createData)
			toast.success(t('partners.messages.createSuccess', 'Partner created successfully'))
			handleCloseCreateDialog()
		} catch (error) {
			const message = error instanceof Error ? error.message : t('errors.generic', 'An unexpected error occurred')
			toast.error(t('common.error', 'Error'), message)
		}
	}

	const handleUpdatePartner = async () => {
		if (!selectedPartner) return

		const errors = validateForm(formData)
		if (Object.keys(errors).length > 0) {
			setFormErrors(errors)
			return
		}

		const updateData: UpdatePartnerData = {
			name: formData.name,
			nameAr: formData.nameAr || undefined,
			contact: {
				phone: formData.phone || undefined,
				mobile: formData.mobile || undefined,
				email: formData.email || undefined,
			},
			address: {
				street: formData.address || undefined,
				city: formData.city || undefined,
				country: formData.country || undefined,
			},
			accounting: {
				taxNumber: formData.taxNumber || undefined,
			},
			notes: formData.notes || undefined,
			isActive: formData.isActive,
		}

		try {
			await updatePartnerMutation.mutateAsync({
				id: selectedPartner.id,
				data: updateData,
			})
			toast.success(t('partners.messages.updateSuccess', 'Partner updated successfully'))
			handleCloseEditDialog()
		} catch (error) {
			const message = error instanceof Error ? error.message : t('errors.generic', 'An unexpected error occurred')
			toast.error(t('common.error', 'Error'), message)
		}
	}

	const handleDeletePartner = async () => {
		if (!selectedPartner) return

		try {
			await deletePartnerMutation.mutateAsync(selectedPartner.id)
			toast.success(t('partners.messages.deleteSuccess', 'Partner deleted successfully'))
			handleCloseDeleteDialog()
		} catch (error) {
			const message = error instanceof Error ? error.message : t('errors.generic', 'An unexpected error occurred')
			toast.error(t('common.error', 'Error'), message)
		}
	}

	const handleExportCSV = useCallback(() => {
		const exportData = partners.map((partner) => ({
			[t('partners.fields.code', 'Code')]: partner.code,
			[t('partners.fields.name', 'Name')]: partner.name,
			[t('partners.fields.nameAr', 'Arabic Name')]: partner.nameAr || '',
			[t('partners.fields.type', 'Type')]: partner.type === 'supplier' ? t('partners.supplier', 'Supplier') : t('partners.customer', 'Customer'),
			[t('partners.fields.phone', 'Phone')]: partner.contact?.phone || '',
			[t('partners.fields.email', 'Email')]: partner.contact?.email || '',
			[t('partners.fields.status', 'Status')]: partner.isActive
				? t('common.active', 'Active')
				: t('common.inactive', 'Inactive'),
		}))

		downloadCSV(exportData, `partners_${new Date().toISOString().split('T')[0]}`)
	}, [partners, t])

	const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const handleSearch = useCallback((value: string) => {
		setSearchInputValue(value)
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current)
		}
		searchTimeoutRef.current = setTimeout(() => {
			setSearchTerm(value)
			setPage(1)
		}, 300)
	}, [])

	useEffect(() => {
		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
			}
		}
	}, [])

	if (isLoading) {
		return (
			<div className="flex items-center justify-center min-h-[400px]" role="status" aria-live="polite">
				<LoadingSpinner size="lg" />
			</div>
		)
	}

	if (error) {
		const errorMessage = error instanceof Error ? error.message : t('errors.generic', 'An unexpected error occurred')
		return (
			<div className="flex flex-col items-center justify-center min-h-[400px] text-center" role="alert">
				<Users className="h-12 w-12 text-gray-400 mb-4" />
				<h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
					{t('common.error', 'Error')}
				</h3>
				<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{errorMessage}</p>
			</div>
		)
	}

	return (
		<div className="space-y-6">
			<PageHeader
				title={t('partners.title', 'Partners')}
				subtitle={t('partners.subtitle', 'Manage suppliers and customers')}
				actions={
					hasPermission('partners:create') && (
						<Button
							onClick={() => setIsCreateDialogOpen(true)}
							leftIcon={<Plus className="h-4 w-4" />}
						>
							{t('partners.addPartner', 'Add Partner')}
						</Button>
					)
				}
			/>

			<Card>
				<CardContent className="p-0">
					<div className="p-4 border-b border-gray-200 dark:border-gray-700">
						<div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
							<div className="relative flex-1 max-w-md">
								<Input
									type="text"
									placeholder={t('partners.searchPlaceholder', 'Search partners...')}
									value={searchInputValue}
									onChange={(e) => handleSearch(e.target.value)}
									leftIcon={<Search className="h-4 w-4" />}
								/>
							</div>
							<div className="flex gap-2">
								<select
									value={typeFilter}
									onChange={(e) => {
										setTypeFilter(e.target.value as any)
										setPage(1)
									}}
									className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-sm"
								>
									<option value="all">{t('partners.allTypes', 'All Types')}</option>
									<option value="supplier">{t('partners.supplier', 'Supplier')}</option>
									<option value="customer">{t('partners.customer', 'Customer')}</option>
								</select>
								<Button variant="outline" onClick={handleExportCSV} leftIcon={<Download className="h-4 w-4" />}>
									{t('common.exportCSV', 'Export CSV')}
								</Button>
							</div>
						</div>
					</div>

					{partners.length === 0 ? (
						<div className="flex flex-col items-center justify-center py-12 text-center">
							<Users className="h-12 w-12 text-gray-400 mb-4" />
							<h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
								{t('partners.noPartners', 'No partners found')}
							</h3>
							<p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm">
								{t('partners.noPartnersDescription', 'Get started by adding your first partner.')}
							</p>
							{hasPermission('partners:create') && (
								<Button className="mt-4" onClick={() => setIsCreateDialogOpen(true)} leftIcon={<Plus className="h-4 w-4" />}>
									{t('partners.addPartner', 'Add Partner')}
								</Button>
							)}
						</div>
					) : (
						<>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>{t('partners.fields.code', 'Code')}</TableHead>
										<TableHead>{t('partners.fields.name', 'Name')}</TableHead>
										<TableHead>{t('partners.fields.type', 'Type')}</TableHead>
										<TableHead>{t('partners.fields.phone', 'Phone')}</TableHead>
										<TableHead>{t('partners.fields.email', 'Email')}</TableHead>
										<TableHead>{t('partners.fields.status', 'Status')}</TableHead>
										<TableHead className="ltr:text-right rtl:text-left">
											{t('common.actions', 'Actions')}
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{partners.map((partner) => (
										<TableRow key={partner.id}>
											<TableCell className="font-medium font-mono">{partner.code}</TableCell>
											<TableCell>
												<div>
													<div className="font-medium">{i18n.language === 'ar' ? partner.nameAr || partner.name : partner.name}</div>
													{partner.nameAr && partner.nameAr !== partner.name && (
														<div className="text-sm text-gray-500">{partner.name}</div>
													)}
												</div>
											</TableCell>
											<TableCell>
												<span
													className={cn(
														'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
														partner.type === 'supplier'
															? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
															: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
													)}
												>
													{partner.type === 'supplier' ? t('partners.supplier', 'Supplier') : t('partners.customer', 'Customer')}
												</span>
											</TableCell>
											<TableCell>{partner.contact?.phone || partner.contact?.mobile || '-'}</TableCell>
											<TableCell>{partner.contact?.email || '-'}</TableCell>
											<TableCell>
												<span
													className={cn(
														'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
														partner.isActive
															? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
															: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
													)}
												>
													{partner.isActive ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
												</span>
											</TableCell>
											<TableCell className="ltr:text-right rtl:text-left">
												<Menu as="div" className="relative inline-block text-left">
													<MenuButton
														as={Button}
														variant="ghost"
														size="icon"
														className="h-8 w-8"
													>
														<span className="sr-only">{t('common.actions', 'Actions')}</span>
														<MoreVertical className="h-4 w-4" />
													</MenuButton>
													<MenuItems
														className={cn(
															'absolute z-10 mt-2 w-48 rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
															isRtl ? 'left-0' : 'right-0'
														)}
													>
														<div className="py-1">
															{hasPermission('partners:update') && (
																<MenuItem>
																	{({ focus }) => (
																		<button
																			onClick={() => handleOpenEditDialog(partner)}
																			className={cn(
																				'w-full flex items-center gap-2 px-4 py-2 text-sm',
																				focus
																					? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
																					: 'text-gray-700 dark:text-gray-300'
																			)}
																		>
																			<Edit2 className="h-4 w-4" />
																			{t('partners.editPartner', 'Edit Partner')}
																		</button>
																	)}
																</MenuItem>
															)}
															{hasPermission('partners:delete') && (
																<MenuItem>
																	{({ focus }) => (
																		<button
																			onClick={() => handleOpenDeleteDialog(partner)}
																			className={cn(
																				'w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400',
																				focus && 'bg-red-50 dark:bg-red-900/20'
																			)}
																		>
																			<Trash2 className="h-4 w-4" />
																			{t('partners.deletePartner', 'Delete Partner')}
																		</button>
																	)}
																</MenuItem>
															)}
														</div>
													</MenuItems>
												</Menu>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>

							{pagination && pagination.totalPages > 1 && (
								<nav
									className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700"
									aria-label={t('pagination.navigation', 'Pagination navigation')}
								>
									<div className="text-sm text-gray-500 dark:text-gray-400">
										{t('common.showing', 'Showing')} {(page - 1) * 20 + 1}-
										{Math.min(page * 20, pagination.total)} {t('common.of', 'of')} {pagination.total}
									</div>
									<div className="flex gap-2">
										<Button
											variant="outline"
											size="sm"
											onClick={() => setPage((p) => Math.max(1, p - 1))}
											disabled={page === 1}
										>
											{t('common.previous', 'Previous')}
										</Button>
										<Button
											variant="outline"
											size="sm"
											onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
											disabled={page === pagination.totalPages}
										>
											{t('common.next', 'Next')}
										</Button>
									</div>
								</nav>
							)}
						</>
					)}
				</CardContent>
			</Card>

			{/* Create Partner Dialog - Simplified for MVP */}
			<Dialog open={isCreateDialogOpen} onClose={handleCloseCreateDialog}>
				<DialogHeader onClose={handleCloseCreateDialog}>
					<DialogTitle>{t('partners.addPartner', 'Add Partner')}</DialogTitle>
					<DialogDescription>
						{t('partners.addPartnerDescription', 'Add a new supplier or customer')}
					</DialogDescription>
				</DialogHeader>
				<DialogContent>
					<div className="space-y-4 max-h-[60vh] overflow-y-auto">
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium mb-1">
									{t('partners.fields.type', 'Type')} <span className="text-red-500">*</span>
								</label>
								<select
									value={formData.type}
									onChange={(e) => setFormData((prev) => ({ ...prev, type: e.target.value as any }))}
									className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800"
								>
									<option value="supplier">{t('partners.supplier', 'Supplier')}</option>
									<option value="customer">{t('partners.customer', 'Customer')}</option>
								</select>
							</div>
							<Input
								label={t('partners.fields.code', 'Code')}
								name="code"
								value={formData.code}
								onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value }))}
								error={formErrors.code}
								isRequired
							/>
						</div>
						<Input
							label={t('partners.fields.name', 'Name (English)')}
							name="name"
							value={formData.name}
							onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
							error={formErrors.name}
							isRequired
						/>
						<Input
							label={t('partners.fields.nameAr', 'Name (Arabic)')}
							name="nameAr"
							value={formData.nameAr}
							onChange={(e) => setFormData((prev) => ({ ...prev, nameAr: e.target.value }))}
							dir="rtl"
						/>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<Input
								label={t('partners.fields.phone', 'Phone')}
								name="phone"
								value={formData.phone}
								onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
							/>
							<Input
								label={t('partners.fields.mobile', 'Mobile')}
								name="mobile"
								value={formData.mobile}
								onChange={(e) => setFormData((prev) => ({ ...prev, mobile: e.target.value }))}
							/>
						</div>
						<Input
							label={t('partners.fields.email', 'Email')}
							name="email"
							type="email"
							value={formData.email}
							onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
						/>
						<Input
							label={t('partners.fields.address', 'Address')}
							name="address"
							value={formData.address}
							onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
						/>
						<Input
							label={t('partners.fields.taxNumber', 'Tax Number')}
							name="taxNumber"
							value={formData.taxNumber}
							onChange={(e) => setFormData((prev) => ({ ...prev, taxNumber: e.target.value }))}
						/>
						<div>
							<label className="block text-sm font-medium mb-1">
								{t('partners.fields.notes', 'Notes')}
							</label>
							<textarea
								name="notes"
								value={formData.notes}
								onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
								rows={3}
								className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
							/>
						</div>
					</div>
				</DialogContent>
				<DialogFooter>
					<Button variant="outline" onClick={handleCloseCreateDialog}>
						{t('common.cancel', 'Cancel')}
					</Button>
					<Button onClick={handleCreatePartner} isLoading={createPartnerMutation.isPending}>
						{t('common.save', 'Save')}
					</Button>
				</DialogFooter>
			</Dialog>

			{/* Edit Partner Dialog - Similar structure, simplified for MVP */}
			<Dialog open={isEditDialogOpen} onClose={handleCloseEditDialog}>
				<DialogHeader onClose={handleCloseEditDialog}>
					<DialogTitle>{t('partners.editPartner', 'Edit Partner')}</DialogTitle>
					<DialogDescription>{selectedPartner?.code}</DialogDescription>
				</DialogHeader>
				<DialogContent>
					<div className="space-y-4 max-h-[60vh] overflow-y-auto">
						<Input
							label={t('partners.fields.code', 'Code')}
							name="code"
							value={formData.code}
							disabled
						/>
						<Input
							label={t('partners.fields.name', 'Name (English)')}
							name="name"
							value={formData.name}
							onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
							error={formErrors.name}
							isRequired
						/>
						<Input
							label={t('partners.fields.nameAr', 'Name (Arabic)')}
							name="nameAr"
							value={formData.nameAr}
							onChange={(e) => setFormData((prev) => ({ ...prev, nameAr: e.target.value }))}
							dir="rtl"
						/>
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<Input
								label={t('partners.fields.phone', 'Phone')}
								name="phone"
								value={formData.phone}
								onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
							/>
							<Input
								label={t('partners.fields.mobile', 'Mobile')}
								name="mobile"
								value={formData.mobile}
								onChange={(e) => setFormData((prev) => ({ ...prev, mobile: e.target.value }))}
							/>
						</div>
						<Input
							label={t('partners.fields.email', 'Email')}
							name="email"
							type="email"
							value={formData.email}
							onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
						/>
						<Input
							label={t('partners.fields.address', 'Address')}
							name="address"
							value={formData.address}
							onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
						/>
						<Input
							label={t('partners.fields.taxNumber', 'Tax Number')}
							name="taxNumber"
							value={formData.taxNumber}
							onChange={(e) => setFormData((prev) => ({ ...prev, taxNumber: e.target.value }))}
						/>
						<div>
							<label className="block text-sm font-medium mb-1">
								{t('partners.fields.notes', 'Notes')}
							</label>
							<textarea
								name="notes"
								value={formData.notes}
								onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
								rows={3}
								className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
							/>
						</div>
					</div>
				</DialogContent>
				<DialogFooter>
					<Button variant="outline" onClick={handleCloseEditDialog}>
						{t('common.cancel', 'Cancel')}
					</Button>
					<Button onClick={handleUpdatePartner} isLoading={updatePartnerMutation.isPending}>
						{t('common.save', 'Save')}
					</Button>
				</DialogFooter>
			</Dialog>

			{/* Delete Confirmation Dialog */}
			<Dialog open={isDeleteDialogOpen} onClose={handleCloseDeleteDialog}>
				<DialogHeader onClose={handleCloseDeleteDialog}>
					<DialogTitle>{t('partners.deletePartner', 'Delete Partner')}</DialogTitle>
					<DialogDescription>
						{t('partners.confirmDelete', 'Are you sure you want to delete this partner?')}
					</DialogDescription>
				</DialogHeader>
				<DialogContent>
					<p className="text-sm text-gray-500 dark:text-gray-400">
						{t('partners.confirmDeleteDescription', 'This action will deactivate the partner. It can be restored later.')}
					</p>
					{selectedPartner && (
						<div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
							<p className="font-medium text-gray-900 dark:text-gray-100">{selectedPartner.code}</p>
							<p className="text-sm text-gray-500 dark:text-gray-400">{selectedPartner.name}</p>
						</div>
					)}
				</DialogContent>
				<DialogFooter>
					<Button variant="outline" onClick={handleCloseDeleteDialog}>
						{t('common.cancel', 'Cancel')}
					</Button>
					<Button
						variant="destructive"
						onClick={handleDeletePartner}
						isLoading={deletePartnerMutation.isPending}
					>
						{t('common.delete', 'Delete')}
					</Button>
				</DialogFooter>
			</Dialog>
		</div>
	)
}
